import React, {useCallback, useContext, useLayoutEffect, useRef} from 'react';
import {Platform, StyleSheet, Text, ScrollView, View} from 'react-native';
import {useNavigation} from '@react-navigation/native';
import {colors} from '../assets/styles/colors';
import SupportGuideHeader from '../components/SupportGuideHeader';
import {StoreContext} from '../components/StoreContextProvider';
import {dateFormat, LANDING_PAGE_URL, SESSION_EXPIRE_TIME} from '../constants';
import NotificationSwitch from "../components/NotificationSwitch";
import AsyncStorage from "@react-native-async-storage/async-storage";
import moment from "moment";
import {User} from "../../generated/smg_service_pb";
import ServiceClient from "../utils/ServiceClient";
import MainStyle from "../../MainStyle";
import {updateUserPayloadType} from "../types";

const NotificationsScreen = () => {
  const navigation: any = useNavigation();
  const Context = useContext(StoreContext);
  const {user, session, cancelModalConfig} = Context;

  // SMG-3480: guard against rapid taps. Each toggle builds a full updateUser
  // payload from the `user` captured in the render closure, so a second tap
  // fired before getUser() has refreshed Context would carry stale values for
  // the other two flags and visibly revert them. While a write+refetch is
  // in flight we no-op further taps (per AC: rapid taps should not respond).
  const pendingRef = useRef(false);

  const onExit = useCallback(() => {
    navigation.navigate('challenges');
  }, []);

  const getUser = () => {
    Context.getUser().then(() => {
      pendingRef.current = false;
      Context.setLoadingOverlay(false);

    }).catch(err => {
      console.log('getUser err', err);
      pendingRef.current = false;
      Context.setLoadingOverlay(false);

      Context.setCancelModalConfig({
        visible: true, title: 'ERROR', leftBtnText: 'OK', subtitle: err.message,
        onExit: () => {
          Context.setCancelModalConfig({...cancelModalConfig, visible: false});
        }
      });
    });
  };

  const onChange = (field: 'text' | 'email' | 'partner') => () => {
    if (!user) return;
    if (pendingRef.current) return;

    // SMG-3480 (F4): the guard ref must never be left stranded. Set it
    // immediately before the network call, and wrap the body so any
    // synchronous throw before/at the call (payload build, or updateUser()
    // throwing inline instead of rejecting) clears the ref — otherwise the
    // screen would wedge, silently no-oping every future tap. The .then/.catch
    // below still own ref-clearing for the async success/failure paths.
    try {
      Context.setLoadingOverlay(true);

      const {username, allowTextNotifications, allowEmailNotifications, allowPartnerPromos, handicap, handedness} = user;
      const {firstname, lastname, dateOfBirth, gender} = user;

      const payload: updateUserPayloadType = {
        username, allowTextNotifications, allowEmailNotifications, allowPartnerPromos, handicap, handedness, firstname,
        lastname, dateOfBirth: moment(dateOfBirth, dateFormat).format(), gender
      };

      if (field === 'text') payload.allowTextNotifications = !payload.allowTextNotifications;
      else if (field === 'email') payload.allowEmailNotifications = !payload.allowEmailNotifications;
      else if (field === 'partner') payload.allowPartnerPromos = !payload.allowPartnerPromos;

      pendingRef.current = true;
      ServiceClient.updateUser(payload).then(() => {
        getUser();

      }).catch(err => {
        console.log('updateUser err', err);
        pendingRef.current = false;
        Context.setLoadingOverlay(false);

        Context.setCancelModalConfig({
          visible: true, title: 'ERROR', leftBtnText: 'OK', subtitle: err.message,
          onExit: () => {
            Context.setCancelModalConfig({...cancelModalConfig, visible: false});
          }
        });
      });
    } catch (err: any) {
      pendingRef.current = false;
      Context.setLoadingOverlay(false);

      Context.setCancelModalConfig({
        visible: true, title: 'ERROR', leftBtnText: 'OK', subtitle: err?.message,
        onExit: () => {
          Context.setCancelModalConfig({...cancelModalConfig, visible: false});
        }
      });
    }
  };

  useLayoutEffect(() => {
    if (!session || !user) {
      Context.setLoadingOverlay(true);
      (async () => {
        const time = await AsyncStorage.getItem('lastActiveTime');

        if (time && moment().diff(moment(time), 'minutes') > SESSION_EXPIRE_TIME) {
          Context.logout().then(() => {
            if(Platform.OS === 'web'){
              window.open(LANDING_PAGE_URL, '_self');
            }
            Context.setLoadingOverlay(false);
          });

        } else {
          const val = await AsyncStorage.getItem('session');
          let curUser: User | undefined;

          if (val) {
            const Session = JSON.parse(val);
            Context.setSession(Session);

            try {
              curUser = await Context.getUser(Session);

            } catch (e: any) {
              Context.setCancelModalConfig({
                visible: true, title: 'Error', subtitle: e?.message || 'Error', rightBtnText: 'OK',
                onResume: () => Context.setCancelModalConfig({...cancelModalConfig, visible: false})
              });
            }
          }

          Context.setLoadingOverlay(false);
          if (!val || !curUser || !curUser.userId) {
            Context.logout().then(() => {
              if(Platform.OS === 'web'){
                window.open(LANDING_PAGE_URL, '_self');
              }
            });
          }
        }
      })();
    }
  }, []);

  return (
    <>
      <SupportGuideHeader title="Notifications" onExit={onExit}/>

      <ScrollView keyboardShouldPersistTaps="handled" contentContainerStyle={[MainStyle.min_h_full, MainStyle.bg_contest]}>
        <View style={[MainStyle.a_i_c, MainStyle.p_t_17]}>
          <Text style={rnStyles.contentTitleSm}>NOTIFICATION SETTINGS</Text>

          <View style={[MainStyle.m_t_50, MainStyle.j_c_s_between, MainStyle.h_240]}>
            <NotificationSwitch isSm label="TEXT" value={user?.allowTextNotifications} onPress={onChange('text')}/>

            <NotificationSwitch isSm label="EMAIL" value={user?.allowEmailNotifications} onPress={onChange('email')}/>

            <NotificationSwitch isSm label="PARTNER PROMOS" value={user?.allowPartnerPromos} onPress={onChange('partner')}/>
          </View>
        </View>
      </ScrollView>
    </>
  );
};

const rnStyles = StyleSheet.create({
  contentTitleSm: {color: colors.lightBlue, fontSize: 16, fontFamily: 'Barlow-Bold'}
});

export default NotificationsScreen;
